import React from 'react';
import { Redirect } from 'react-router-dom';
import { I18NContext } from '../../I18NContext';
import {
    CancellationReasonFilterOption,
    OrderSummaryPageLabels,
    CancelBottomSheetDetails,
    CancellationResult
} from '../component';
import CancelFormSelectV2 from './CancelFormSelectV2';
import cancelOrder from '../../accessors/CancelOrderAccessor';
import { CancellationDropdown, CancellationDropdownItem } from './CancellationDropdown';

import appMetrics from '../../common/MetricsService';
import MetricsConstants from '../../common/MetricsConstants';
import * as KatalMetrics from '@amzn/katal-metrics';
import OrderHistoryRedirectionComponentRow from '../order/OrderHistoryRedirectionComponent'
import {hasAnnotation} from "./PaymentUtils";

interface CancelFormV2Props {
    cancelDetails: CancelBottomSheetDetails;
    orderSummaryPageLabels: OrderSummaryPageLabels;
    refresh?: () => void;
    onCancellationComplete?: (result: CancellationResult) => void;
    toggleDropdown: (isOpen: boolean) => void;
    isPillPack?: boolean;
}

interface CancelFormV2State {
    selectorRef: React.RefObject<any>;
    disableButton: boolean;
    alert: any;
    selectedOrderIds: Set<string>;
    cancelFilterOption?: CancellationReasonFilterOption;
}

class CancelFormV2 extends React.Component<CancelFormV2Props, CancelFormV2State> {

    constructor(props: CancelFormV2Props) {
        super(props);

        const initialSelectedIds = new Set<string>();

        this.state = {
            selectorRef: React.createRef(),
            alert: false,
            disableButton: props.cancelDetails.cancellableMedications ? true : false,
            selectedOrderIds: initialSelectedIds,
            cancelFilterOption: undefined,
        }
    }

    setCancelReason = (cancelFilterValue: string) => {
        const newOption = this.props.orderSummaryPageLabels.cancelForm.cancelReason.cancelReasonFilterOptions
            .find((option) => option.value === cancelFilterValue);
        this.setState({ cancelFilterOption: newOption });
    };

    render() {
        const cancelReason = this.props.orderSummaryPageLabels.cancelForm.cancelReason;
        const showCancelDrop = cancelReason && cancelReason.cancelReasonFilterOptions;
        const button = this.state.disableButton || (showCancelDrop && !this.state.cancelFilterOption) ?
            <pui-button spacingTop='small' disabled onClick={() => this._onSubmit()} label={this.props.orderSummaryPageLabels.cancelForm.cancelButton} /> :
            <pui-button id="button-to-cancel" data-csa-c-type="button" data-csa-c-action="cancel" data-csa-c-content-id="button-to-cancel" data-csa-c-slot-id="main"
                        spacingTop='small' onClick={() => this._onSubmit()} label={this.props.orderSummaryPageLabels.cancelForm.cancelButton} />

        return (<pui-stack direction='vertical' mainAxisArrangement='start' secondaryAxisArrangement='start'
            flex="true">
            {this.state.alert}
            <pui-heading input={this.props.isPillPack ? this.props.orderSummaryPageLabels.cancelForm.pillPackCancelModalTitle : this.props.orderSummaryPageLabels.cancelForm.cancelTitle} textSize='extra-large' textAlign='left'/>
                {this.props.isPillPack &&
                    <pui-text input={this.props.orderSummaryPageLabels.cancelForm.pillPackCancelModalBody} spacingTop='small' spacingBottom='small' />
                }
                {this.isAutoRefill() &&
                    (<pui-stack mainaxisarrangement="start" secondaryaxisarrangement="start" spacingTop="medium"
                                direction='horizontal'>
                <pui-section spacingTop="mini" spacingRight="small" style={{'width':'5%', 'max-width':'5%'}}>
                <pui-icon imgClass="status-warning-icon"></pui-icon>
                </pui-section>
                <pui-stack pui-stack direction='vertical'>
                <pui-text input={this.props.orderSummaryPageLabels.cancelForm.cancelFormTitle} fontWeight="bold"></pui-text>
                <pui-text input={this.props.orderSummaryPageLabels.cancelForm.cancelFormMessage}  textAlign='left'/>
                </pui-stack>
             </pui-stack>)
             }
                {this._createCancellationCheckbox()}

                {showCancelDrop && (
                    <pui-section style={{ width: '100%' }}>
                        <CancellationDropdown
                        label={`${cancelReason.cancelReasonLabel}`}
                        placeHolderLabel={`${cancelReason.cancelReasonSelectLabel}`}
                        onSelectionChange={this.setCancelReason}
                        selectedOption={this.state.cancelFilterOption}
                        onToggle={this.props.toggleDropdown}
                        >
                        {cancelReason.cancelReasonFilterOptions.map((filterOption) => (
                            <CancellationDropdownItem key={filterOption.value} filterOption={filterOption} />
                        ))}
                        </CancellationDropdown>
                    </pui-section>
                    )}
            {button}
        </pui-stack>
        )
    }

    _createCancellationCheckbox() {
        const { cancellableMedications } = this.props.cancelDetails;
        if (cancellableMedications && cancellableMedications.length > 0) {
            return (
                <pui-selector spacingBottom="medium" spacingTop="medium" multiselect ref={this.state.selectorRef} style={{ width: '100%' }}>
                    {cancellableMedications.map((med, i) => (
                        <CancelFormSelectV2
                            key={`cancel-med-${i}`}
                            initialSelected={false}
                            selectButton={(disable: boolean) => this._toggleMedSelection(med.id!, disable)}
                            cancelFormSelectItem={{
                                headline: med.headline,
                                subline: med.subline,
                                autoRefillLine: ""
                            }}
                            isAutoRefill={hasAnnotation(med.annotations, 'AutoRefill')}
                        />
                    ))}
                </pui-selector>
            );
        }
        return (
            <pui-selector spacingBottom="medium" spacingTop="medium" multiselect ref={this.state.selectorRef} style={{ width: '100%' }}>
                <CancelFormSelectV2 selectButton={(disable: boolean) => this._selectItem(disable)}
                                    cancelFormSelectItem={this.createCancelFormSelectItem()}
                                    isAutoRefill={this.isAutoRefill()}/>
            </pui-selector>
        );
    }

    _toggleMedSelection(orderId: string, deselected: boolean) {
        this.setState((prevState) => {
            const updated = new Set(prevState.selectedOrderIds);
            if (deselected) {
                if (!updated.has(orderId)) return null;
                updated.delete(orderId);
            } else {
                if (updated.has(orderId)) return null;
                updated.add(orderId);
            }
            return { selectedOrderIds: updated, disableButton: updated.size === 0 };
        });
    }

    createCancelFormSelectItem = () => {
        return {
            headline: this.props.cancelDetails.cancellationGroup.headline,
            subline: this.props.cancelDetails.cancellationGroup.subline,
            autoRefillLine: this.props.cancelDetails.cancellationGroup.autoRefillLine || ""
        }
    }

    _selectItem(disableButton: boolean) {
        this.setState({ disableButton });
    }

    _getCancelledCount(): number {
        const { cancellableMedications, orderIds } = this.props.cancelDetails;
        if (cancellableMedications && cancellableMedications.length > 0) {
            return this.state.selectedOrderIds.size;
        }
        return orderIds.length;
    }

    _onSubmit() {
        const cancelReason = this.state.cancelFilterOption ? `${this.state.cancelFilterOption?.value}` : null;
        const actionMetricsPublisher = appMetrics.getMetricsPublisherForMethod("CancelOrder");
        const attemptMetric = new KatalMetrics.Metric.TimedAttempt(MetricsConstants.METRIC_CANCEL_ORDER_REQUEST).withMonitor();

        this.setState({
            alert: false,
            disableButton: true,
        });

        const cancellableMedications = this.props.cancelDetails.cancellableMedications;
        const isPerMedFlow = !!(cancellableMedications && cancellableMedications.length > 0);
        const orderIds = isPerMedFlow
            ? Array.from(this.state.selectedOrderIds)
            : this.props.cancelDetails.orderIds;
        // Full when every cancellable medication is selected; partial otherwise. The legacy
        // (non per-med) flow always cancels the whole order, so it is treated as full.
        const isFullCancellation = isPerMedFlow && cancellableMedications
            ? orderIds.length === cancellableMedications.length
            : true;
        const purchaseId = this.props.cancelDetails.purchaseId;
        cancelOrder(purchaseId, orderIds, cancelReason, this.props.cancelDetails.csrfToken, isFullCancellation)
            .then((response: any) => {
                if ("CANCEL_PENDING" === response.cancellationStatus) {
                    if (this.isAutoRefill()) {
                        this.setState({
                            alert: <Redirect to={{
                                    pathname: '/',
                                    state: { orderHistoryRedirectionComponentRow: <OrderHistoryRedirectionComponentRow isRedirected={true} redirectionMessage={this.props.orderSummaryPageLabels.redirectCancellationMessages.cancellationPendingAutoRefillRedirectMessage} redirectionTitle={this.props.orderSummaryPageLabels.redirectCancellationMessages.cancellationPendingAutoRefillRedirectTitle}></OrderHistoryRedirectionComponentRow> }
                                    }}/>
                        })
                    } else {
                        if (this.props.onCancellationComplete) {
                            this.props.onCancellationComplete({ status: 'pending', cancelledCount: this._getCancelledCount() });
                        } else {
                            this.setState({
                                alert: <pui-box spacingBottom="small" style={{ width: '85%' }} theme="info">
                                    <pui-text input={this.props.orderSummaryPageLabels.cancelForm.cancelPending} />
                                </pui-box>
                            });
                        }
                    }
                    if (this.props.refresh) {
                        this.props.refresh();
                    }
                } else {
                    if (this.isAutoRefill()) {
                        this.setState({
                            alert: <Redirect to={{
                                    pathname: '/',
                                    state: { orderHistoryRedirectionComponentRow: <OrderHistoryRedirectionComponentRow isRedirected={true} redirectionMessage={this.props.orderSummaryPageLabels.redirectCancellationMessages.cancellationSuccessAutoRefillRedirectMessage} redirectionTitle={this.props.orderSummaryPageLabels.redirectCancellationMessages.cancellationSuccessRedirectTitle}></OrderHistoryRedirectionComponentRow>}
                                    }}/>
                        })
                    } else {
                        if (this.props.onCancellationComplete) {
                            this.props.onCancellationComplete({ status: 'success', cancelledCount: this._getCancelledCount() });
                        } else {
                            this.setState({
                                alert: <pui-box spacingBottom="small" style={{ width: '85%' }} theme="success">
                                    <pui-text input={this.props.orderSummaryPageLabels.cancelForm.cancelSuccess} />
                                </pui-box>
                            });
                        }
                    }
                }
                attemptMetric.setSuccess();
                actionMetricsPublisher.publish(attemptMetric);
            })
            .catch(() => {
                this.setState({
                    alert: <pui-box style={{ width: '85%' }} theme="alert">
                        <pui-text input={this.props.orderSummaryPageLabels.cancelForm.cancelFailed} />
                    </pui-box>,
                    disableButton: false,
                });
                attemptMetric.setFailure();
                actionMetricsPublisher.publish(attemptMetric);
            });
        
    }

    isAutoRefill() {
        return hasAnnotation(this.props.cancelDetails.cancellationGroup.annotations, 'AutoRefill')
    }
}

CancelFormV2.contextType = I18NContext;

export default CancelFormV2;
